import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactPlayer from 'react-player';
import ReactSlider from 'react-slider';
import Cropper from 'react-easy-crop';
import VideoCaptionsOverlay from '../VideoCaptionsOverlay';
import { VideoCaption } from '../VideoCaptionsManager';
import { cn } from "../../lib/utils";
import { Loader2, AlertCircle, FileX } from 'lucide-react';

// CSS to hide video controls only when in trimmer mode, otherwise show native controls
const videoControlsStyle = `
  .video-player-hide-controls::-webkit-media-controls-panel {
    display: none !important;
  }
  .video-player-hide-controls::-webkit-media-controls-play-button {
    display: none !important;
  }
  .video-player-hide-controls::-webkit-media-controls-timeline {
    display: none !important;
  }
  .video-player-hide-controls::-webkit-media-controls-current-time-display {
    display: none !important;
  }
  .video-player-hide-controls::-webkit-media-controls-time-remaining-display {
    display: none !important;
  }
  .video-player-hide-controls::-webkit-media-controls-mute-button {
    display: none !important;
  }
  .video-player-hide-controls::-webkit-media-controls-volume-slider {
    display: none !important;
  }
  .video-player-hide-controls::-webkit-media-controls-fullscreen-button {
    display: none !important;
  }
  .video-player-hide-controls::-webkit-media-controls {
    display: none !important;
  }
  /* Custom range input styles for video seeker */
  .custom-video-seeker {
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: transparent;
    outline: none;
    -webkit-appearance: none;
    appearance: none;
    cursor: pointer;
  }
  .custom-video-seeker::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #ffffff;
    cursor: pointer;
    border: 1px solid #cbd5e1;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    transition: box-shadow 0.2s;
  }
  .custom-video-seeker::-webkit-slider-thumb:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  }
  .custom-video-seeker::-webkit-slider-thumb:active {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  }
  .custom-video-seeker::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #ffffff;
    cursor: pointer;
    border: 1px solid #cbd5e1;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    transition: box-shadow 0.2s;
  }
  .custom-video-seeker::-moz-range-thumb:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  }
  .custom-video-seeker::-moz-range-thumb:active {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  }
  .custom-video-seeker::-webkit-slider-runnable-track {
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: transparent;
  }
  .custom-video-seeker::-moz-range-track {
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: transparent;
  }
  .horizontal-slider {
    width: 100%;
    height: 18px;
    margin: 0 auto;
    cursor: pointer;
    position: relative;
    user-select: none;
    touch-action: none;
  }
  .horizontal-slider .slider-track {
    position: relative;
    top: 6px;
    height: 6px;
    background: #334155;
    border-radius: 3px;
  }
  .horizontal-slider .slider-track.slider-track-1 {
    background: hsl(var(--primary));
  }
  .horizontal-slider .slider-thumb {
    position: absolute;
    z-index: 2;
    top: 0px;
    width: 18px;
    height: 18px;
    background-color: hsl(var(--primary));
    border: 2px solid #fff;
    border-radius: 50%;
    box-shadow: 0 2px 4px hsl(var(--primary) / 0.3);
    cursor: grab;
    outline: none;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .horizontal-slider .slider-thumb:hover {
    box-shadow: 0 4px 8px hsl(var(--primary) / 0.4);
  }
  .horizontal-slider .slider-thumb:active {
    box-shadow: 0 4px 12px hsl(var(--primary) / 0.5);
    cursor: grabbing;
  }
`;

// Inject styles on component mount
if (typeof document !== 'undefined') {
  const styleId = 'video-player-always-show-controls-style';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = videoControlsStyle;
    document.head.appendChild(style);
  }
}

interface VideoPlayerProps {
  videoUrl: string;
  videoDuration: number;
  // Trimming props
  enableTrimming?: boolean;
  startTime?: number;
  endTime?: number;
  onStartTimeChange?: (startTime: number) => void;
  onEndTimeChange?: (endTime: number) => void;
  // Playback props
  isPlaying?: boolean;
  onPlayingChange?: (isPlaying: boolean) => void;
  currentTime?: number;
  onCurrentTimeChange?: (currentTime: number) => void;
  onSeek?: (time: number) => void;
  // Captions props
  captions?: VideoCaption[];
  selectedCaptionId?: string | null; // ID of the caption being edited/selected
  // Mobile format props
  needsMobileFormat?: boolean;
  cropData?: { x: number; y: number; width: number; height: number; aspect: number } | null;
  showStoryOverlay?: boolean;
  onVideoDimensionsChange?: (dimensions: { width: number; height: number }) => void;
  // Container click handler
  onVideoClick?: () => void;
  // Show slider below video (read-only indicator)
  showSlider?: boolean;
  // Show trimmer (editable slider) - only for trim editor
  showTrimmer?: boolean;
  // Crop and zoom props for applying transformations
  crop?: { x: number; y: number } | null;
  zoom?: number | null;
  onCropChange?: (cropData: any, crop: { x: number; y: number } | null, zoom: number) => void;
}

export default function VideoPlayer({
  videoUrl,
  videoDuration,
  enableTrimming = false,
  startTime: externalStartTime = 0,
  endTime: externalEndTime,
  onStartTimeChange,
  onEndTimeChange,
  isPlaying: externalIsPlaying,
  onPlayingChange,
  currentTime: externalCurrentTime,
  onCurrentTimeChange,
  onSeek,
  captions = [],
  selectedCaptionId = null,
  needsMobileFormat = false,
  cropData = null,
  showStoryOverlay = true,
  onVideoDimensionsChange,
  onVideoClick,
  showSlider = true,
  showTrimmer = false,
  crop = null,
  zoom = null,
  onCropChange,
}: VideoPlayerProps): JSX.Element {
  const playerRef = useRef<any>(null);
  const nativeVideoRef = useRef<HTMLVideoElement | null>(null);
  const cropperVideoRef = useRef<HTMLVideoElement | null>(null);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  
  // Internal state for Cropper integration
  const [cropperCrop, setCropperCrop] = useState({ x: 0, y: 0 });
  const [cropperZoom, setCropperZoom] = useState(1);
  const [cropperPixels, setCropperPixels] = useState<any>(null);

  const onCropComplete = useCallback((_croppedArea: any, croppedAreaPixels: any) => {
    setCropperPixels(croppedAreaPixels);
    if (onCropChange) {
      onCropChange(croppedAreaPixels, cropperCrop, cropperZoom);
    }
  }, [onCropChange, cropperCrop, cropperZoom]);

  useEffect(() => {
    if (crop) setCropperCrop(crop);
    if (zoom) setCropperZoom(zoom);
  }, [crop, zoom]);

  useEffect(() => {
    if (cropData) {
      // If we have cropData, we don't necessarily set cropperCrop/cropperZoom
      // because cropData is in pixels. But Cropper needs percent/x,y.
      // However, if we just want to SHOW the crop area, we can use cropData.
    }
  }, [cropData]);

  const [internalStartTime, setInternalStartTime] = useState<number>(0);
  const [internalEndTime, setInternalEndTime] = useState<number>(videoDuration);
  const [sliderValue, setSliderValue] = useState<[number, number]>([0, videoDuration]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  
  const [internalIsPlaying, setInternalIsPlaying] = useState<boolean>(false);
  const [internalCurrentTime, setInternalCurrentTime] = useState<number>(0);
  const [playerReady, setPlayerReady] = useState<boolean>(false);
  const [videoDimensions, setVideoDimensions] = useState<{ width: number; height: number } | null>(null);
  const [videoTransform, setVideoTransform] = useState<React.CSSProperties>({});
  const [loadError, setLoadError] = useState<boolean>(false);
  
  const previousStartTimeRef = useRef<number>(0);
  const previousEndTimeRef = useRef<number>(videoDuration);
  const isDraggingRef = useRef<boolean>(false);
  const lastSeekTimeRef = useRef<number | null>(null);
  const isDraggingSliderRef = useRef<boolean>(false);
  
  const hasTrimRange = (externalStartTime !== undefined && externalStartTime > 0) || 
                       (externalEndTime !== undefined && externalEndTime < videoDuration);
  
  const startTime = externalStartTime !== undefined 
    ? externalStartTime 
    : (enableTrimming ? internalStartTime : 0);
  const endTime = externalEndTime !== undefined 
    ? externalEndTime 
    : (enableTrimming ? internalEndTime : videoDuration);
  const isPlaying = externalIsPlaying !== undefined ? externalIsPlaying : internalIsPlaying;
  const currentTime = externalCurrentTime !== undefined ? externalCurrentTime : internalCurrentTime;
  
  const isBlobUrl = videoUrl.startsWith('blob:');
  const isApiUrl = videoUrl.includes('/api/social/media');
  const shouldUseNativeVideo = isBlobUrl || isApiUrl;
  const PlayerComponent = ReactPlayer as any;
  
  useEffect(() => {
    if (videoDimensions && onVideoDimensionsChange) {
      onVideoDimensionsChange(videoDimensions);
    }
  }, [videoDimensions, onVideoDimensionsChange]);

  useEffect(() => {
    if (playerReady && (hasTrimRange || enableTrimming) && startTime >= 0 && startTime < videoDuration) {
      const timeoutId = setTimeout(() => {
        if (shouldUseNativeVideo && nativeVideoRef.current) {
          if (Math.abs(nativeVideoRef.current.currentTime - startTime) > 0.1) {
            nativeVideoRef.current.currentTime = startTime;
            if (onCurrentTimeChange) {
              onCurrentTimeChange(startTime);
            } else {
              setInternalCurrentTime(startTime);
            }
          }
        } else if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
          playerRef.current.seekTo(startTime, 'seconds');
          if (onCurrentTimeChange) {
            onCurrentTimeChange(startTime);
          } else {
            setInternalCurrentTime(startTime);
          }
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [playerReady, hasTrimRange, enableTrimming, startTime, videoDuration, isBlobUrl, onCurrentTimeChange]);

  useEffect(() => {
    if (needsMobileFormat && cropperVideoRef.current) {
      if (isPlaying) {
        cropperVideoRef.current.play().catch(() => {});
      } else {
        cropperVideoRef.current.pause();
      }
    }
  }, [isPlaying, needsMobileFormat]);

  useEffect(() => {
    if (needsMobileFormat && cropperVideoRef.current && !isDraggingRef.current && !isDraggingSliderRef.current) {
      if (Math.abs(cropperVideoRef.current.currentTime - currentTime) > 0.3) {
        cropperVideoRef.current.currentTime = currentTime;
      }
    }
  }, [currentTime, needsMobileFormat]);

  useEffect(() => {
    const calculateTransform = () => {
      // Don't apply CSS transform when cropData exists - the crop overlay handles preview
      // The CSS transform would misalign the overlay with the video
      if (cropData) {
        setVideoTransform({});
        return;
      }

      if (zoom && videoContainerRef.current && videoDimensions) {
        const effectiveCrop: { x: number; y: number } = crop || { x: 0, y: 0 };

        // If zoom is 1 and no crop, don't apply transform to ensure perfect centering
        if (zoom <= 1 && effectiveCrop.x === 0 && effectiveCrop.y === 0) {
          setVideoTransform({});
          return;
        }

        const containerRect = videoContainerRef.current.getBoundingClientRect();
        // ... rest of calculation ...
        const transformStyle: React.CSSProperties = {
          transform: `translate(${effectiveCrop.x}px, ${effectiveCrop.y}px) scale(${zoom})`,
          transformOrigin: 'center center',
        };

        setVideoTransform(transformStyle);
      } else {
        setVideoTransform({});
      }
    };

    calculateTransform();
    window.addEventListener('resize', calculateTransform);
    const timeoutId = setTimeout(calculateTransform, 100);
    return () => {
      window.removeEventListener('resize', calculateTransform);
      clearTimeout(timeoutId);
    };
  }, [crop, zoom, videoDimensions, cropData]);
  
  useEffect(() => {
    if (enableTrimming && videoDuration > 0 && !isInitialized) {
      const initialStart = onStartTimeChange ? externalStartTime : 0;
      const initialEnd = onEndTimeChange ? (externalEndTime ?? videoDuration) : videoDuration;
      setInternalStartTime(initialStart);
      setInternalEndTime(initialEnd);
      setSliderValue([initialStart, initialEnd]);
      previousStartTimeRef.current = initialStart;
      previousEndTimeRef.current = initialEnd;
      if (onStartTimeChange) onStartTimeChange(initialStart);
      if (onEndTimeChange) onEndTimeChange(initialEnd);
      setIsInitialized(true);
    }
  }, [enableTrimming, videoDuration, isInitialized, externalStartTime, externalEndTime, onStartTimeChange, onEndTimeChange]);
  
  useEffect(() => {
    if (enableTrimming && !isDraggingRef.current && videoDuration > 0 && isInitialized) {
      const newValue: [number, number] = [
        Math.max(0, Math.min(startTime, videoDuration)),
        Math.max(0, Math.min(endTime, videoDuration))
      ];
      
      if (Math.abs(newValue[0] - sliderValue[0]) > 0.001 || Math.abs(newValue[1] - sliderValue[1]) > 0.001) {
        setSliderValue(newValue);
      }
    }
  }, [enableTrimming, startTime, endTime, videoDuration, isInitialized]);
  
  const handleSliderChange = (value: number | number[]) => {
    if (!enableTrimming) return;
    if (!isDraggingRef.current) isDraggingRef.current = true;
    
    if (Array.isArray(value) && value.length === 2) {
      let [newStart, newEnd] = value;
      if (isNaN(newStart) || isNaN(newEnd) || newStart < 0 || newEnd < 0) return;
      
      newStart = Math.max(0, Math.min(newStart, videoDuration));
      newEnd = Math.max(0, Math.min(newEnd, videoDuration));
      
      const minGap = 0.1;
      if (newStart >= newEnd || (newEnd - newStart) < minGap) {
        const prevStart = previousStartTimeRef.current;
        const prevEnd = previousEndTimeRef.current;
        const startDiff = Math.abs(newStart - prevStart);
        const endDiff = Math.abs(newEnd - prevEnd);
        
        if (startDiff > endDiff) {
          newEnd = Math.min(newStart + minGap, videoDuration);
        } else {
          newStart = Math.max(newEnd - minGap, 0);
        }
      }
      
      if (newStart === 0 && newEnd === 0) {
        newStart = previousStartTimeRef.current;
        newEnd = previousEndTimeRef.current;
        if (newStart === 0 && newEnd === 0) return;
      }
      
      const finalGap = newEnd - newStart;
      if (newStart >= newEnd || finalGap < 0.1) return;
      
      setSliderValue([newStart, newEnd]);
      const prevStart = previousStartTimeRef.current;
      const prevEnd = previousEndTimeRef.current;
      const startDiff = Math.abs(newStart - prevStart);
      const endDiff = Math.abs(newEnd - prevEnd);
      const threshold = 0.1;
      let seekToTime: number | null = null;
      
      if (startDiff > threshold && startDiff > endDiff) {
        seekToTime = newStart;
      } else if (endDiff > threshold && endDiff > startDiff) {
        seekToTime = newEnd;
      } else if (startDiff > threshold && endDiff <= threshold) {
        seekToTime = newStart;
      } else if (endDiff > threshold && startDiff <= threshold) {
        seekToTime = newEnd;
      }
      
      if (onStartTimeChange) {
        setInternalStartTime(newStart);
        onStartTimeChange(newStart);
      } else {
        setInternalStartTime(newStart);
      }
      
      if (onEndTimeChange) {
        setInternalEndTime(newEnd);
        onEndTimeChange(newEnd);
      } else {
        setInternalEndTime(newEnd);
      }
      
      previousStartTimeRef.current = newStart;
      previousEndTimeRef.current = newEnd;
      
      if (playerReady && seekToTime !== null) {
        lastSeekTimeRef.current = seekToTime;
        try {
          if (needsMobileFormat && showStoryOverlay && cropperVideoRef.current) {
            cropperVideoRef.current.currentTime = seekToTime;
          } else if (shouldUseNativeVideo && nativeVideoRef.current) {
            nativeVideoRef.current.currentTime = seekToTime;
          } else if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
            playerRef.current.seekTo(seekToTime, 'seconds');
          }
        } catch (error) {
          console.warn('Error seeking video:', error);
        }
      }
    }
  };
  
  const handleSliderAfterChange = () => {
    if (!enableTrimming) return;
    isDraggingRef.current = false;
    const [finalStart, finalEnd] = sliderValue;
    if (finalStart >= 0 && finalEnd > finalStart && finalEnd <= videoDuration) {
      if (onStartTimeChange) onStartTimeChange(finalStart);
      if (onEndTimeChange) onEndTimeChange(finalEnd);
    }
    setTimeout(() => { lastSeekTimeRef.current = null; }, 500);
  };
  
  const handleProgress = (state: { played: number; playedSeconds: number; loaded: number; loadedSeconds: number }) => {
    let newTime = state.playedSeconds;
    if (hasTrimRange || enableTrimming) {
      if (newTime < startTime) {
        newTime = startTime;
      } else if (newTime >= endTime) {
        newTime = endTime;
        if (playerReady && !isDraggingRef.current && !isDraggingSliderRef.current && lastSeekTimeRef.current === null) {
          if (onPlayingChange) {
            onPlayingChange(false);
          } else {
            setInternalIsPlaying(false);
          }
          try {
            if (playerRef.current && typeof playerRef.current.pause === 'function') {
              playerRef.current.pause();
            }
            if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
              playerRef.current.seekTo(startTime, 'seconds');
            }
          } catch (error) {
            console.warn('Error pausing/seeking video:', error);
          }
        }
      }
    }
    if (onCurrentTimeChange) {
      onCurrentTimeChange(newTime);
    } else {
      setInternalCurrentTime(newTime);
    }
  };
  
  const handlePlay = () => {
    if (!playerReady) {
      if (onPlayingChange) onPlayingChange(true);
      else setInternalIsPlaying(true);
      return;
    }
    try {
      if (!isDraggingRef.current && lastSeekTimeRef.current === null) {
        if (shouldUseNativeVideo && nativeVideoRef.current) {
          const currentTime = nativeVideoRef.current.currentTime;
          if ((hasTrimRange || enableTrimming) && (currentTime >= endTime || currentTime < startTime)) {
            nativeVideoRef.current.currentTime = startTime;
          }
          nativeVideoRef.current.play();
        } else if (playerRef.current) {
          const currentTime = typeof playerRef.current.getCurrentTime === 'function' ? playerRef.current.getCurrentTime() : 0;
          if ((hasTrimRange || enableTrimming) && currentTime !== undefined && (currentTime >= endTime || currentTime < startTime)) {
            if (typeof playerRef.current.seekTo === 'function') {
              playerRef.current.seekTo(startTime, 'seconds');
            }
          }
        }
      }
      if (onPlayingChange) onPlayingChange(true);
      else setInternalIsPlaying(true);
    } catch (error) {
      console.warn('Error accessing player methods:', error);
      if (onPlayingChange) onPlayingChange(true);
      else setInternalIsPlaying(true);
    }
  };
  
  useEffect(() => {
    if (shouldUseNativeVideo && nativeVideoRef.current) {
      if (isPlaying) nativeVideoRef.current.play().catch(console.error);
      else nativeVideoRef.current.pause();
    }
  }, [isPlaying, shouldUseNativeVideo]);
  
  useEffect(() => {
    if (shouldUseNativeVideo && nativeVideoRef.current) {
      const video = nativeVideoRef.current;
      const keepControlsVisible = () => {
        if (video) {
          const event = new MouseEvent('mousemove', { bubbles: true, cancelable: true, view: window });
          video.dispatchEvent(event);
        }
      };
      const interval = setInterval(keepControlsVisible, 2000);
      const showControls = () => { if (video) video.style.cursor = 'pointer'; };
      video.addEventListener('mouseenter', showControls);
      video.addEventListener('mousemove', showControls);
      video.addEventListener('click', showControls);
      return () => {
        clearInterval(interval);
        video.removeEventListener('mouseenter', showControls);
        video.removeEventListener('mousemove', showControls);
        video.removeEventListener('click', showControls);
      };
    }
  }, [shouldUseNativeVideo, videoUrl]);
  
  const handlePause = () => {
    if (onPlayingChange) onPlayingChange(false);
    else setInternalIsPlaying(false);
  };
  
  const handleSeek = (time: number) => {
    try {
      let clampedTime = time;
      if (hasTrimRange || enableTrimming) clampedTime = Math.max(startTime, Math.min(time, endTime));
      
      if (needsMobileFormat && cropperVideoRef.current) {
        cropperVideoRef.current.currentTime = clampedTime;
        if (onCurrentTimeChange) onCurrentTimeChange(clampedTime);
        else setInternalCurrentTime(clampedTime);
      } else if (shouldUseNativeVideo && nativeVideoRef.current) {
        nativeVideoRef.current.currentTime = clampedTime;
        if (onCurrentTimeChange) onCurrentTimeChange(clampedTime);
        else setInternalCurrentTime(clampedTime);
      } else if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
        playerRef.current.seekTo(clampedTime, 'seconds');
        if (onCurrentTimeChange) onCurrentTimeChange(clampedTime);
        else setInternalCurrentTime(clampedTime);
      }
      if (onSeek) onSeek(clampedTime);
    } catch (error) {
      console.warn('Error seeking video:', error);
    }
  };
  
  const handleVideoClick = () => {
    if (!playerReady) return;
    if (isDraggingRef.current || lastSeekTimeRef.current !== null) return;
    if (onVideoClick) {
      onVideoClick();
      return;
    }

    if (needsMobileFormat && cropperVideoRef.current) {
      if (isPlaying) cropperVideoRef.current.pause();
      else {
        const currentTime = cropperVideoRef.current.currentTime;
        if (enableTrimming && (currentTime >= endTime || currentTime < startTime)) {
          cropperVideoRef.current.currentTime = startTime;
        }
        cropperVideoRef.current.play().catch(console.warn);
      }
      return;
    }

    const activeVideo = shouldUseNativeVideo ? nativeVideoRef.current : null;

    if (activeVideo) {
      if (isPlaying) activeVideo.pause();
      else {
        const currentTime = activeVideo.currentTime;
        if (enableTrimming && (currentTime >= endTime || currentTime < startTime)) {
          activeVideo.currentTime = startTime;
        }
        activeVideo.play().catch(console.warn);
      }
    } else if (playerRef.current) {
      if (isPlaying) {
        if (onPlayingChange) onPlayingChange(false);
        else setInternalIsPlaying(false);
      } else {
        const currentTime = playerRef.current.getCurrentTime();
        if (enableTrimming && (currentTime >= endTime || currentTime < startTime)) {
          playerRef.current.seekTo(startTime, 'seconds');
        }
        if (onPlayingChange) onPlayingChange(true);
        else setInternalIsPlaying(true);
      }
    }
  };
  
  const handleNativeTimeUpdate = () => {
    if (nativeVideoRef.current) {
      let newTime = nativeVideoRef.current.currentTime;
      if (hasTrimRange || enableTrimming) {
        if (newTime < startTime) {
          newTime = startTime;
          nativeVideoRef.current.currentTime = startTime;
        } else if (newTime >= endTime) {
          newTime = endTime;
          if (!isDraggingRef.current && lastSeekTimeRef.current === null) {
            if (onPlayingChange) onPlayingChange(false);
            else setInternalIsPlaying(false);
            nativeVideoRef.current.pause();
            nativeVideoRef.current.currentTime = startTime;
          }
        }
      }
      if (onCurrentTimeChange) onCurrentTimeChange(newTime);
      else setInternalCurrentTime(newTime);
    }
  };
  
  return (
    <div className="w-full">
      <div 
        ref={videoContainerRef}
        onClick={handleVideoClick}
        className={cn(
          "overflow-hidden bg-black relative flex items-center justify-center cursor-pointer rounded-2xl shadow-2xl border border-border transition-all duration-500 mx-auto",
          "w-full aspect-video",
          showSlider ? "mb-2" : "mb-0"
        )}
      >
        {loadError ? (
          <div className="flex flex-col items-center justify-center p-8 text-center space-y-4">
            <div className="p-6 bg-muted rounded-[2rem] border-2 border-dashed border-border">
              <FileX className="h-12 w-12 text-muted-foreground" />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-black text-muted-foreground uppercase tracking-tighter">Media Unavailable</h3>
              <p className="text-xs text-muted-foreground font-medium max-w-[250px]">
                The video file could not be loaded. It may have been moved or deleted from the server.
              </p>
            </div>
          </div>
        ) : videoUrl ? (
          needsMobileFormat ? (
            <div className="absolute inset-0 z-10">
              <Cropper
                image={undefined}
                video={videoUrl}
                crop={cropperCrop}
                zoom={cropperZoom}
                aspect={9 / 16}
                onCropChange={setCropperCrop}
                onZoomChange={setCropperZoom}
                onCropComplete={onCropComplete}
                showGrid={showStoryOverlay}
                objectFit="contain"
                {...({
                  videoProps: {
                    ref: cropperVideoRef,
                    autoPlay: false,
                    muted: true,
                    playsInline: true,
                    onTimeUpdate: (e: any) => {
                      if (!isDraggingSliderRef.current && !isDraggingRef.current) {
                        let newTime = e.currentTarget.currentTime;
                        if (hasTrimRange || enableTrimming) {
                          if (newTime < startTime) {
                            newTime = startTime;
                            e.currentTarget.currentTime = startTime;
                          } else if (newTime >= endTime) {
                            newTime = endTime;
                            if (onPlayingChange) onPlayingChange(false);
                            else setInternalIsPlaying(false);
                            e.currentTarget.pause();
                            e.currentTarget.currentTime = startTime;
                          }
                        }
                        if (onCurrentTimeChange) onCurrentTimeChange(newTime);
                        else setInternalCurrentTime(newTime);
                      }
                    },
                    onPlay: () => {
                      if (onPlayingChange) onPlayingChange(true);
                      else setInternalIsPlaying(true);
                    },
                    onPause: () => {
                      if (onPlayingChange) onPlayingChange(false);
                      else setInternalIsPlaying(false);
                    },
                  }
                } as any)}
                style={{
                  containerStyle: { background: '#000' },
                  mediaStyle: { objectFit: 'contain' },
                  cropAreaStyle: { 
                    border: '2px solid hsl(var(--primary) / 0.5)',
                    boxShadow: '0 0 0 9999em rgba(0, 0, 0, 0.7)'
                  }
                }}
              />
            </div>
          ) : shouldUseNativeVideo ? (
            <video
              ref={nativeVideoRef}
              src={videoUrl}
              preload="metadata"
              className={cn(
                "z-10 transition-all duration-500",
                "max-w-full max-h-full object-contain"
              )}
              style={videoTransform}
              onLoadedMetadata={(e) => {
                setPlayerReady(true);
                const video = e.currentTarget;
                setVideoDimensions({ width: video.videoWidth, height: video.videoHeight });
                setTimeout(() => {
                  if ((hasTrimRange || enableTrimming) && startTime >= 0 && startTime < videoDuration) {
                    video.currentTime = startTime;
                    if (onCurrentTimeChange) onCurrentTimeChange(startTime);
                    else setInternalCurrentTime(startTime);
                  }
                }, 50);
              }}
              onTimeUpdate={handleNativeTimeUpdate}
              onPlay={() => {
                if (onPlayingChange) onPlayingChange(true);
                else setInternalIsPlaying(true);
                if (nativeVideoRef.current && !isDraggingRef.current && lastSeekTimeRef.current === null) {
                  const currentTime = nativeVideoRef.current.currentTime;
                  if ((hasTrimRange || enableTrimming) && (currentTime >= endTime || currentTime < startTime)) {
                    nativeVideoRef.current.currentTime = startTime;
                  }
                }
              }}
              onPause={handlePause}
              onError={(e) => {
                console.error('Native video error:', e);
                setPlayerReady(false);
                setLoadError(true);
              }}
              controls={false}
            />
          ) : (
            <PlayerComponent
              ref={playerRef}
              url={videoUrl}
              playing={isPlaying}
              controls={false}
              width="100%"
              height="auto"
              style={{ 
                maxHeight: '500px', 
                ...videoTransform, 
                zIndex: 10,
                objectFit: 'contain'
              }}
              onProgress={handleProgress as any}
              onPlay={handlePlay}
              onPause={handlePause}
              onReady={() => {
                setPlayerReady(true);
                setLoadError(false);
                const tryGetDimensions = () => {
                  if (playerRef.current && playerRef.current.getInternalPlayer) {
                    try {
                      const internalPlayer = playerRef.current.getInternalPlayer();
                      if (internalPlayer) {
                        if (internalPlayer.videoWidth && internalPlayer.videoHeight) {
                          setVideoDimensions({ width: internalPlayer.videoWidth, height: internalPlayer.videoHeight });
                          return;
                        }
                        if (internalPlayer.tagName === 'IFRAME' && internalPlayer.contentWindow) {
                          setTimeout(tryGetDimensions, 100);
                        }
                      }
                    } catch (e) { console.warn('Error getting ReactPlayer dimensions:', e); }
                  }
                };
                tryGetDimensions();
                if ((hasTrimRange || enableTrimming) && startTime >= 0 && startTime < videoDuration && playerRef.current && typeof playerRef.current.seekTo === 'function') {
                  setTimeout(() => {
                    if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
                      playerRef.current.seekTo(startTime, 'seconds');
                      if (onCurrentTimeChange) onCurrentTimeChange(startTime);
                      else setInternalCurrentTime(startTime);
                    }
                  }, 200);
                }
              }}
              onDuration={(duration: number) => {
                if (playerRef.current && playerRef.current.getInternalPlayer) {
                  try {
                    const internalPlayer = playerRef.current.getInternalPlayer();
                    if (internalPlayer && internalPlayer.videoWidth && internalPlayer.videoHeight) {
                      setVideoDimensions({ width: internalPlayer.videoWidth, height: internalPlayer.videoHeight });
                    }
                  } catch (e) { console.warn('Error getting dimensions from onDuration:', e); }
                }
              }}
              onError={(error: any) => {
                console.error('ReactPlayer error:', error);
                setPlayerReady(false);
                setLoadError(true);
              }}
              light={false}
              pip={false}
              stopOnUnmount={false}
            />
          )
        ) : (
          <div className="text-muted-foreground py-20">No video URL provided</div>
        )}

        {/* Crop preview overlay - shows darkened areas outside the crop region */}
        {!needsMobileFormat && cropData && videoDimensions && videoDimensions.width > 0 && (() => {
          // Convert crop pixel coords to percentages of the video dimensions
          const cropXPct = (cropData.x / videoDimensions.width) * 100;
          const cropYPct = (cropData.y / videoDimensions.height) * 100;
          const cropWPct = (cropData.width / videoDimensions.width) * 100;
          const cropHPct = (cropData.height / videoDimensions.height) * 100;

          // Calculate the video's rendered area within the aspect-video container
          // Container is always 16:9 (aspect-video), video uses object-contain
          const videoAspect = videoDimensions.width / videoDimensions.height;
          const containerAspect = 16 / 9;
          let videoLeftPct = 0, videoTopPct = 0, videoWPct = 100, videoHPct = 100;

          if (videoAspect > containerAspect) {
            // Video is wider: full width, letterboxed vertically
            const renderedHRatio = containerAspect / videoAspect;
            videoHPct = renderedHRatio * 100;
            videoTopPct = (100 - videoHPct) / 2;
          } else if (videoAspect < containerAspect) {
            // Video is taller: full height, pillarboxed horizontally
            const renderedWRatio = videoAspect / containerAspect;
            videoWPct = renderedWRatio * 100;
            videoLeftPct = (100 - videoWPct) / 2;
          }

          // Map crop percentages from video space to container space
          const left = videoLeftPct + (cropXPct / 100) * videoWPct;
          const top = videoTopPct + (cropYPct / 100) * videoHPct;
          const w = (cropWPct / 100) * videoWPct;
          const h = (cropHPct / 100) * videoHPct;

          return (
            <div className="absolute inset-0 z-20 pointer-events-none">
              {/* Top dark band */}
              <div className="absolute bg-black/70" style={{ left: 0, top: 0, width: '100%', height: `${top}%` }} />
              {/* Bottom dark band */}
              <div className="absolute bg-black/70" style={{ left: 0, top: `${top + h}%`, width: '100%', bottom: 0 }} />
              {/* Left dark band */}
              <div className="absolute bg-black/70" style={{ left: 0, top: `${top}%`, width: `${left}%`, height: `${h}%` }} />
              {/* Right dark band */}
              <div className="absolute bg-black/70" style={{ left: `${left + w}%`, top: `${top}%`, right: 0, height: `${h}%` }} />
              {/* Crop border */}
              <div
                className="absolute border-2 border-primary/50 rounded-sm"
                style={{ left: `${left}%`, top: `${top}%`, width: `${w}%`, height: `${h}%` }}
              />
            </div>
          );
        })()}

        {captions.length > 0 && videoContainerRef.current && (
          <VideoCaptionsOverlay
            captions={captions}
            currentTime={currentTime}
            videoContainerRef={videoContainerRef}
            videoDimensions={videoDimensions}
          />
        )}
      </div>
      
      {showSlider && !showTrimmer && externalStartTime !== undefined && externalEndTime !== undefined && (() => {
        const currentTimeValue = externalCurrentTime !== undefined ? externalCurrentTime : internalCurrentTime;
        const hasTrim = externalStartTime > 0 || (externalEndTime !== undefined && externalEndTime < videoDuration);
        const progressPercent = (currentTimeValue / videoDuration) * 100;
        
        return (
          <div className="mb-8 px-4">
            <div className="relative h-12 flex items-center w-full">
              <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1.5 bg-card rounded-full" />
              {hasTrim && (
                <div
                  className="absolute top-1/2 -translate-y-1/2 h-1.5 bg-emerald-500/80 rounded-full z-10"
                  style={{
                    left: `${(externalStartTime / videoDuration) * 100}%`,
                    width: `${((externalEndTime - externalStartTime) / videoDuration) * 100}%`,
                  }}
                />
              )}
              <div
                className="absolute left-0 top-1/2 -translate-y-1/2 h-1.5 bg-slate-600 rounded-full z-[1]"
                style={{ width: `${progressPercent}%` }}
              />
              <input
                type="range"
                min={0}
                max={videoDuration}
                step={0.1}
                value={currentTimeValue}
                onChange={(e) => {
                  const newTime = parseFloat(e.target.value);
                  isDraggingSliderRef.current = true;
                  if (onCurrentTimeChange) onCurrentTimeChange(newTime);
                  else setInternalCurrentTime(newTime);
                  
                  if (needsMobileFormat && cropperVideoRef.current) {
                    cropperVideoRef.current.currentTime = newTime;
                  } else if (nativeVideoRef.current && shouldUseNativeVideo) {
                    nativeVideoRef.current.currentTime = newTime;
                  } else if (playerRef.current && playerRef.current.seekTo && !shouldUseNativeVideo) {
                    playerRef.current.seekTo(newTime);
                  }
                }}
                onMouseDown={() => { isDraggingSliderRef.current = true; }}
                onMouseUp={(e) => {
                  const newTime = parseFloat((e.target as HTMLInputElement).value);
                  isDraggingSliderRef.current = false;
                  if (onSeek) onSeek(newTime);
                  if (onCurrentTimeChange) onCurrentTimeChange(newTime);
                  else setInternalCurrentTime(newTime);
                  
                  if (needsMobileFormat && cropperVideoRef.current) {
                    cropperVideoRef.current.currentTime = newTime;
                  } else if (nativeVideoRef.current && shouldUseNativeVideo) {
                    nativeVideoRef.current.currentTime = newTime;
                  } else if (playerRef.current && playerRef.current.seekTo && !shouldUseNativeVideo) {
                    playerRef.current.seekTo(newTime);
                  }
                }}
                onTouchStart={() => { isDraggingSliderRef.current = true; }}
                onTouchEnd={(e) => {
                  const newTime = parseFloat((e.target as HTMLInputElement).value);
                  isDraggingSliderRef.current = false;
                  if (onSeek) onSeek(newTime);
                  if (onCurrentTimeChange) onCurrentTimeChange(newTime);
                  else setInternalCurrentTime(newTime);
                  
                  if (needsMobileFormat && cropperVideoRef.current) {
                    cropperVideoRef.current.currentTime = newTime;
                  } else if (nativeVideoRef.current && shouldUseNativeVideo) {
                    nativeVideoRef.current.currentTime = newTime;
                  } else if (playerRef.current && playerRef.current.seekTo && !shouldUseNativeVideo) {
                    playerRef.current.seekTo(newTime);
                  }
                }}
                className="custom-video-seeker relative z-20 -top-[1.5px]"
              />
            </div>
          </div>
        );
      })()}
      
      {showTrimmer && enableTrimming && (
        <div className="mb-8">
          {selectedCaptionId && captions.length > 0 && (() => {
            const selectedCaption = captions.find(c => c.id === selectedCaptionId);
            if (!selectedCaption) return null;
            const startPercent = (selectedCaption.startTime / videoDuration) * 100;
            const endPercent = (selectedCaption.endTime / videoDuration) * 100;
            const widthPercent = endPercent - startPercent;
            return (
              <div className="px-2 mb-1 mx-2 relative h-2 overflow-hidden">
                <div
                  className="absolute h-1 bg-emerald-500/60 rounded-sm top-1/2 -translate-y-1/2"
                  style={{ left: `${startPercent}%`, width: `${widthPercent}%` }}
                />
              </div>
            );
          })()}
          
          <div className="px-2 mb-4 mx-2 relative">
            <ReactSlider
              className="horizontal-slider"
              thumbClassName="slider-thumb"
              trackClassName="slider-track"
              min={0}
              max={videoDuration}
              step={0.1}
              value={sliderValue}
              onChange={handleSliderChange}
              onAfterChange={handleSliderAfterChange}
            />
          </div>
        </div>
      )}
    </div>
  );
}
